import { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import Modal from './Modal';

/**
 * Reusable confirmation dialog. Used instead of window.confirm() so the look matches
 * the rest of the app and works on mobile / inside scroll-locked modals.
 *
 * Usage:
 *   const [confirmState, setConfirmState] = useState(null);
 *   setConfirmState({ title: '...', message: '...', onConfirm: async () => { ... } });
 *   <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
 */
export default function ConfirmDialog({ state, onClose }) {
  const [working, setWorking] = useState(false);
  if (!state) return null;

  const {
    title = 'Are you sure?',
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
    icon: Icon = danger ? Trash2 : AlertTriangle,
    onConfirm,
  } = state;

  const handleConfirm = async () => {
    setWorking(true);
    try {
      await onConfirm?.();
      onClose?.();
    } finally {
      setWorking(false);
    }
  };

  return (
    <Modal
      open
      onClose={working ? () => {} : onClose}
      title={title}
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={working}>
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={working}
            className={danger ? 'btn-danger' : 'btn-primary'}
          >
            <Icon size={14} /> {working ? 'Working...' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
            danger
              ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-300'
              : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-300'
          }`}
        >
          <Icon size={20} />
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-300">{message}</div>
      </div>
    </Modal>
  );
}
